/**
 * Presentation helpers shared by both portals.
 *
 * The state-to-tone maps live here rather than in each view so that a pod shown as
 * amber on the customer's dashboard is amber on the operator's too. Divergent colour
 * coding between the two portals is a real support hazard: an engineer reading a
 * customer's screenshot must be able to trust that green means the same thing.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

/**
 * Pod state to tone.
 *
 * Transitional states are `info` rather than `warning`: a pod that is starting is not
 * a problem, and colouring it amber trains people to ignore amber.
 */
export function podStateTone(state: string): Tone {
  switch (state) {
    case 'RUNNING':
      return 'success';
    case 'REQUESTED':
    case 'PLACING':
    case 'RESERVED':
    case 'PREPARING':
    case 'STARTING':
      return 'info';
    case 'STOPPING':
    case 'DESTROYING':
    case 'CLEANING':
      return 'warning';
    case 'FAILED':
    case 'LOST':
      return 'danger';
    case 'STOPPED':
    case 'DESTROYED':
    default:
      return 'neutral';
  }
}

export function acceleratorStateTone(state: string): Tone {
  switch (state) {
    case 'AVAILABLE':
      return 'success';
    case 'ALLOCATED':
    case 'RESERVED':
      return 'accent';
    case 'CLEANING':
    case 'DRAINING':
    case 'MAINTENANCE':
      return 'warning';
    case 'QUARANTINED':
    case 'UNHEALTHY':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function nodeStateTone(state: string): Tone {
  switch (state) {
    case 'READY':
      return 'success';
    case 'VALIDATING':
    case 'REGISTERING':
      return 'info';
    case 'SUSPECT':
    case 'DRAINING':
    case 'DRAINED':
    case 'MAINTENANCE':
      return 'warning';
    case 'OFFLINE':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function healthTone(health: string): Tone {
  switch (health) {
    case 'HEALTHY':
      return 'success';
    case 'DEGRADED':
      return 'warning';
    case 'QUARANTINED':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function vendorTone(vendor: string | null): 'nvidia' | 'amd' | 'neutral' {
  if (vendor === 'NVIDIA') return 'nvidia';
  if (vendor === 'AMD') return 'amd';
  return 'neutral';
}

/** Compact duration: "4h 12m", "38s". Never "0h 0m 38s". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Relative time. Falls back to a date beyond a week, where "9 days ago" stops helping. */
export function formatRelative(iso: string | Date | null): string {
  if (iso === null) return '—';
  const then = typeof iso === 'string' ? new Date(iso) : iso;
  const seconds = (Date.now() - then.getTime()) / 1000;

  if (seconds < 45) return 'just now';
  if (seconds < 90) return 'a minute ago';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.round(seconds / 86_400)}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Formats a decimal money string for display.
 *
 * Takes a string rather than a number: the API sends exact decimals, and parsing them
 * into a float here would reintroduce the very rounding error the backend's bigint
 * ledger exists to avoid.
 */
export function formatMoney(decimal: string | null | undefined, currency = 'USD'): string {
  if (decimal === null || decimal === undefined) return '—';
  const value = Number(decimal);
  if (!Number.isFinite(value)) return '—';
  // Sub-cent amounts are common for per-second GPU pricing, so show enough precision
  // to distinguish $0.0004 from $0.0009 rather than rendering both as $0.00.
  const fractionDigits = value !== 0 && Math.abs(value) < 0.01 ? 4 : 2;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}
